import { ButtonGroup, PrimaryButton } from "@digipost/dds-buttons-react";
import { Checkbox } from "@digipost/dds-checkbox-react";
import { Heading } from "@digipost/dds-heading-react";
import { Spinner } from "@digipost/dds-spinner-react";
import { useLanguage } from "@digipost/translate";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ValidationRules } from "../../../types/entrypoint";
import { Krr } from "../../../types/session";
import {
  SiteImproveActions,
  SiteImproveCategories,
  siteImproveEvent,
} from "../analytics/SiteImprove";
import InfoWarningText from "../InfoWarningText";
import { Dispatch } from "../infrastructure/redux/actions";
import { completePersonRegistrationAction } from "../infrastructure/redux/app/app-actions";
import { RegistrationStatus } from "../infrastructure/redux/org/org-actions";
import { ReduxState } from "../infrastructure/redux/state";
import { setPersonUserInputAction } from "../infrastructure/redux/user/user-actions";
import Translated from "../infrastructure/Translated";
import { useRootPath } from "../infrastructure/useRootPath";
import { useTranslate } from "../infrastructure/useTranslate";
import { ContactFormPerson } from "../registrer/ContactFormPerson";
import styles from "./PersonForm.module.css";

export const PersonForm: FC = () => {
  const translate = useTranslate();
  const rootPath = useRootPath();
  const { language } = useLanguage();

  const [godkjent, setGodkjent] = useState<boolean>(false);
  const [showAcceptGodkjent, setShowAcceptGodkjent] = useState<boolean>(false);
  const [showValidationErrors, setShowValidationErrors] =
    useState<boolean>(false);

  const personUserInput = useSelector((state: ReduxState) => state.user);
  const validationRules = useSelector(
    (state: ReduxState) => state.app.validationRules,
  );
  const registrationStatus = useSelector(
    (state: ReduxState) => state.org.registrationStatus,
  );

  const dispatch = useDispatch<Dispatch>();

  const { nationalNumber, epost, countryCode } =
    personUserInput?.info?.krr || {};

  const isValidFormValue = (
    value: string,
    required: boolean,
    type: keyof ValidationRules,
  ): boolean => {
    const validationRule = validationRules[type];
    const emptyAndRequired = !value && required;
    return (
      !emptyAndRequired &&
      (!validationRule || !value || RegExp(validationRule).test(value))
    );
  };

  const [validations, setValidations] = useState<{
    epost: boolean;
    phone: boolean;
  }>(
    personUserInput?.info?.krr?.noKrr
      ? { epost: true, phone: true }
      : {
          epost: !!epost && isValidFormValue(epost.trim(), true, "email"),
          phone:
            !!nationalNumber &&
            !!countryCode &&
            isValidFormValue(nationalNumber, true, "phoneNumber"),
        },
  );

  useEffect(() => {
    document.title = `Digipost - ${translate(
      "register.person.confirm.header",
    )}`;

    if (!nationalNumber || !countryCode) {
      dispatch(
        setPersonUserInputAction({
          countryCode: countryCode ?? "+47",
          nationalNumber: nationalNumber ?? "",
        }),
      );
    }
  }, [countryCode, dispatch, nationalNumber, translate]);

  const validerOgSend = (event: React.ChangeEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const epostValid = !!epost && isValidFormValue(epost.trim(), true, "email");
    const phoneValid =
      !!nationalNumber &&
      !!countryCode &&
      isValidFormValue(nationalNumber, true, "phoneNumber");

    setShowValidationErrors(true);
    setShowAcceptGodkjent(!godkjent);
    setValidations({ phone: phoneValid, epost: epostValid });

    setValidations({ phone: phoneValid, epost: epostValid });

    if (phoneValid && epostValid && godkjent) {
      dispatch(completePersonRegistrationAction(rootPath, language));
    } else {
      siteImproveEvent({
        category: SiteImproveCategories.PERSON_REGISTRATION,
        action: SiteImproveActions.NEW_ACCOUNT,
        label: "/person/valideringsfeil",
      });
    }
  };

  const isInfoFromKRR = personUserInput?.info?.krr?.noKrr === undefined;

  const isSubmitting = registrationStatus === RegistrationStatus.SUBMITTING;

  return (
    <form onSubmit={validerOgSend}>
      <Heading level={1} size="large" className={styles.header}>
        <Translated textKey="register.person.confirm.header" />
      </Heading>
      {registrationStatus === RegistrationStatus.FAILED && (
        <InfoWarningText textkey="generic.error" />
      )}
      {registrationStatus === RegistrationStatus.REGISTRANT_TOO_YOUNG && (
        <InfoWarningText textkey="register.error.registrant_too_young" />
      )}
      {registrationStatus === RegistrationStatus.REGISTRANT_MISSES_NAME && (
        <InfoWarningText textkey="register.error.registrant_misses_name" />
      )}
      <Heading level={2} size="small">
        {isInfoFromKRR ? (
          <Translated textKey="register.person.confirm.title" />
        ) : (
          <Translated textKey="register.person.confirm.title_missing" />
        )}
      </Heading>

      <div>
        <Translated textKey="register.person.confirm.info" allowHtml={true} />
      </div>

      <ContactFormPerson
        onChange={(changes: Partial<Krr>) => {
          setShowValidationErrors(false);
          dispatch(setPersonUserInputAction(changes));
        }}
        epost={personUserInput?.info?.krr?.epost}
        phone={personUserInput?.info?.krr?.nationalNumber}
        phoneLandCode={personUserInput?.info?.krr?.countryCode}
        epostValid={!showValidationErrors || validations.epost}
        phoneValid={!showValidationErrors || validations.phone}
      />

      <div className={styles.emailInfoWrapper}>
        <FontAwesomeIcon icon={faInfoCircle} className={styles.emailInfoIcon} />
        <div className={styles.emailInfoText}>
          <Translated textKey={"register.person.info_email_verification"} />
        </div>
      </div>

      <Checkbox
        checked={godkjent}
        onChange={() => {
          setGodkjent(!godkjent);
          setShowAcceptGodkjent(godkjent);
        }}
        data-testid="person-accept-terms-checkbox"
      >
        <Translated textKey="register.person.confirm.avtale" allowHtml={true} />
      </Checkbox>

      {showAcceptGodkjent && (
        <InfoWarningText textkey="register.org.intro.must_accept_terms" />
      )}

      <ButtonGroup>
        <PrimaryButton
          type="submit"
          data-testid="person-create-account-button"
          disabled={isSubmitting}
          iconRight={
            isSubmitting && <Spinner size="small" aria-busy={"true"} />
          }
        >
          <Translated textKey="register.person.confirm.registrer_button" />
        </PrimaryButton>
      </ButtonGroup>
    </form>
  );
};
